import BaseStore from '@src/utils/store/base-store';
import useMount from '@src/utils/hooks/use-mount';
import useModal from '@src/utils/hooks/use-modal';
import LoginStore from '@src/utils/store/login-store';
import Toast from '@src/components/toast';
import formatRoute from '@src/components/route/format-route';
import routeConfigs from '@src/pages/app-router/route-configs';
import rootStore from '@src/mobx-store/root-store';
import { computed, observable } from 'mobx';
import { autobind } from 'core-decorators';
import teamService from '@src/services/team';
import JoinTeamModal from './components/join-team-modal';
import JoinTeamModalStore from './components/join-team-modal/store';
import $$tracker from '@src/tracker';
import { getJoinTeamFailureReason, resolveTrackerContext } from '@src/tracker/business-context';
import FriendRecallModal from '@src/business-components/friend-recall-modal';
import FriendRecallModalStore from '@src/business-components/friend-recall-modal/store';
import BusinessAccountInfoStore from '@src/business-components/business-account-info';

const EMPTY_MEMBERS = [null, null, null, null];
const INVALID_INVITE_MESSAGE = '邀请信息无效，请重新打开邀请链接';
const UNKNOWN_PLAYER_MESSAGE = '玩家身份异常，请稍后重试';
// 活动阶段已知时的提示文案，与 get_config 错误码 -10153/-10154 的后端口径对齐
const ACTIVITY_STAGE_MESSAGES = {
  notStarted: '活动未开始',
  ended: '活动已结束'
};

function createEmptyTeamViewModel() {
  return {
    teamId: '',
    memberCount: 0,
    isFull: false,
    teamStatus: 0,
    members: EMPTY_MEMBERS.slice()
  };
}

function getErrorMessage(error, fallback = '加载失败，请稍后重试') {
  return error && typeof error.message === 'string' && error.message ? error.message : fallback;
}

function getRuntimeXpass() {
  return typeof window === 'undefined' ? null : window.XPASS;
}

class GuestStore extends BaseStore {
  activitySessionStore;
  teamService;
  loginStore = new LoginStore();
  toast = Toast;

  // 页面右上角登录/登出入口；业务 store 已绑定 XPASS 身份、第三方登出规则与右上角绝对定位样式
  accountInfoStore = new BusinessAccountInfoStore();

  @observable.ref inviteContext = null;
  @observable.ref teamViewModel = createEmptyTeamViewModel();
  @observable loadStatus = 'idle';
  @observable activityStage = 'unknown';
  @observable joined = false;
  @observable joinCommitted = false;
  @observable isJoining = false;
  @observable invalidInvite = false;
  @observable isAuthenticating = false;

  initializePromise = null;
  initializeGeneration = null;
  joinPromise = null;
  loginSuccessPromise = null;
  inviteContextResolved = false;

  trackerContext = null;

  pageExposureReported = false;
  lifecycleGeneration = 0;
  isDisposed = false;
  loginFlowGeneration = 0;
  activeLoginFlowId = 0;
  activeLoginRequestOwner = null;
  manualLoginCloseHandler = null;
  joinTeamModalStore;
  friendRecallModalStore;
  joinTeamModal;
  friendRecallModal;

  /**
   * 构建 Guest 页 owner 及其两个页面持有的弹窗实例。
   * @param {object} options 根会话与可替换的服务边界。
   * @param {object} options.activitySessionStore 共享活动事实 owner。
   * @param {object} options.teamFactStore 用户级队伍事实快照（小程序分享 reaction 数据源）。
   * @param {object} options.teamService Guest 队伍查询与变更适配器。
   */
  constructor({ activitySessionStore, teamFactStore = rootStore.teamFactStore, teamService: guestTeamService = teamService } = {}) {
    super();
    this.activitySessionStore = activitySessionStore;
    this.teamFactStore = teamFactStore;
    this.teamService = guestTeamService;
    this.joinTeamModalStore = new JoinTeamModalStore({
      confirmJoin: this.joinInvitedTeam,
      onSuccessClose: this.stayAfterJoin,
      onGoHome: this.navigateToHome
    });
    this.friendRecallModalStore = new FriendRecallModalStore({ onCreatePoster: this.enterPosterFromGuest });
  }

  /**
   * 注册页面持有的弹窗，并为本页挂载开启一个初始化代际。
   * @returns {undefined} 清理由 React hooks 负责；Store 只使迟到的异步工作失效。
   */
  use() {
    super.use();
    this.joinTeamModal = useModal(JoinTeamModal, {
      store: this.joinTeamModalStore,
      onRequestClose: this.joinTeamModalStore.requestClose
    });
    this.friendRecallModal = useModal(FriendRecallModal, {
      store: this.friendRecallModalStore,
      onRequestClose: this.friendRecallModalStore.requestClose
    });
    useMount(() => {
      const generation = this.activateLifecycle();
      this.initialize(generation);
      return () => this.disposeLifecycle(generation);
    });
  }

  /** 四个稳定槽位，由 MoGuestPage 直接消费。 */
  @computed
  get members() {
    return this.teamViewModel.members;
  }

  /**
   * 邀请人信息区的邀请人视图（2026-09-10 契约）：带 `is_inviter` 标记的成员
   * 以与成员槽位一致的头像/昵称形态渲染；无成员带标时为 null
   *（邀请未携带 share_code，或分享人已离队）——PSD 占位框保留。
   */
  @computed
  get inviter() {
    return this.teamViewModel.members.find(member => member && member.isInviter) || null;
  }

  /**
   * 页面流程与邀请上下文自身的门禁；满员（isFull）与组队时间状态（teamStatus）刻意不拦截
   * （2026-09-22 用户决策）：二者是服务端实时竞争状态，页面快照必然可能过期，点击照常
   * 走确认与提交，由后端返回 -20126/-20108/-20109 等错误文案并经 joinInvitedTeam 的 Toast 统一提示。
   */
  @computed
  get joinDisabled() {
    return (
      this.loadStatus === 'loading' ||
      this.isJoining ||
      this.isAuthenticating ||
      this.isDisposed ||
      this.invalidInvite ||
      this.joinCommitted ||
      this.activityStage !== 'active' ||
      !this.teamViewModel.teamId
    );
  }

  /** Guest 数据或变更进行中时，页面建队入口保持 inert。 */
  @computed
  get createDisabled() {
    return this.loadStatus === 'loading' || this.isJoining || this.isDisposed;
  }

  /**
   * 仅解析传给本页面 owner 的 HashRouter history 快照。
   * @param {object} history 含 location.search 的 React Router history。
   * @returns {{entrySource:string,fromTeamId:string,shareCode:string}} 受控邀请字段。
   */
  resolveInviteContext(history) {
    const search = history && history.location && typeof history.location.search === 'string' ? history.location.search : '';
    const params = new URLSearchParams(search.charAt(0) === '?' ? search.slice(1) : search);
    const readString = key => {
      const value = params.get(key);
      return typeof value === 'string' ? value.trim() : '';
    };
    const entrySource = readString('entry_source');
    return {
      // 埋点来源细分不进入后端协议，get_config 仍按既有 team_share 处理队伍邀请。
      entrySource: ['poster_qr', 'invite_link'].indexOf(entrySource) !== -1 ? 'team_share' : entrySource,
      fromTeamId: readString('from_team_id'),
      shareCode: readString('share_code')
    };
  }

  /** 仅返回 Guest 专属归因，直进与未知流量不进入邀请埋点。 */
  getGuestTrackerArgs() {
    const context = this.trackerContext || {};
    const inviteContext = this.inviteContext || {};
    return ['poster_qr', 'invite_link', 'recall_sms'].indexOf(context.source) === -1
      ? null
      : [context.source, context.reqId || '', inviteContext.shareCode || ''];
  }

  /**
   * 先刷新会话归因，再在本路由生命周期内加载一次被邀请的队伍。
   * @param {number} generation 允许提交页面状态的挂载代际。
   * @returns {Promise<object|undefined>} 权威的 Guest Team ViewModel；自有反馈或已销毁后返回 undefined。
   */
  @autobind
  initialize(generation = this.lifecycleGeneration) {
    if (!this.isLifecycleActive(generation)) {
      return Promise.resolve(undefined);
    }
    if (this.initializePromise && this.initializeGeneration === generation) {
      return this.initializePromise;
    }
    if (!this.inviteContextResolved) {
      this.inviteContext = this.resolveInviteContext(this.$history);
      this.trackerContext = resolveTrackerContext(this.$history);
      this.inviteContextResolved = true;
    }
    this.loadStatus = 'loading';
    const request = Promise.resolve()
      .then(() => this.activitySessionStore.refresh(this.inviteContext))
      .then(activityContext => {
        if (!this.isLifecycleActive(generation)) {
          return undefined;
        }
        this.applyActivityContext(activityContext);
        if (!this.inviteContext.fromTeamId) {
          this.invalidInvite = true;
          throw new Error(INVALID_INVITE_MESSAGE);
        }
        return this.refreshGuestTeam(generation);
      })
      .catch(error => {
        if (!this.isLifecycleActive(generation)) {
          return undefined;
        }
        this.invalidInvite = true;
        this.teamViewModel = createEmptyTeamViewModel();
        this.loadStatus = 'error';
        this.toast.fail(getErrorMessage(error));
        return undefined;
      })
      .then(result => {
        if (this.initializePromise === request) {
          this.initializePromise = null;
          this.initializeGeneration = null;
        }
        return result;
      });
    this.initializePromise = request;
    this.initializeGeneration = generation;
    return request;
  }

  /**
   * 加载一份权威的 Guest Team ViewModel，仅成功响应才提交。
   * @param {number} generation 允许提交响应的挂载代际。
   * @param {object|null} requestOwner 可选的登录流程 token，响应仍须归其所有才提交。
   * @returns {Promise<object|undefined>} 已提交的 ViewModel；已销毁后返回 undefined。
   */
  @autobind
  refreshGuestTeam(generation = this.lifecycleGeneration, requestOwner = null) {
    if (!this.isRequestOwnerActive(generation, requestOwner)) {
      return Promise.resolve(undefined);
    }
    if (!this.inviteContext || !this.inviteContext.fromTeamId) {
      return Promise.reject(new Error(INVALID_INVITE_MESSAGE));
    }
    this.loadStatus = 'loading';
    // 2026-09-10 契约：透传邀请链接携带的 share_code，后端据此把分享者标记为 is_inviter（缺省容忍）
    return this.teamService
      .loadGuestTeamContext(this.inviteContext.fromTeamId, this.inviteContext.shareCode)
      .then(viewModel => {
        if (!this.isRequestOwnerActive(generation, requestOwner)) {
          return undefined;
        }
        this.teamViewModel = viewModel;
        this.invalidInvite = false;
        this.loadStatus = 'ready';
        const trackerArgs = this.getGuestTrackerArgs();
        if (!this.pageExposureReported && trackerArgs) {
          $$tracker.pageGuestJoinPage(...trackerArgs);
          this.pageExposureReported = true;
        }
        return viewModel;
      })
      .catch(error => {
        if (!this.isRequestOwnerActive(generation, requestOwner)) {
          return undefined;
        }
        throw error;
      });
  }

  /**
   * 已登录用户直接打开确认；游客先走一次公共 XPASS 登录流程。
   * @returns {*} XPASS 返回值、确认结果；任一页面门控生效时返回 undefined。
   */
  @autobind
  handleJoinClick() {
    if (this.joinDisabled) {
      return undefined;
    }
    const trackerArgs = this.getGuestTrackerArgs();
    if (trackerArgs) {
      $$tracker.clickGuestJoinTeam(...trackerArgs);
    }
    if (this.loginStore.isLogin) {
      return this.openJoinConfirm();
    }
    this.isAuthenticating = true;
    const flowId = ++this.loginFlowGeneration;
    this.activeLoginFlowId = flowId;
    this.loginSuccessPromise = null;
    this.bindLoginHandlers(flowId);
    try {
      return this.loginStore.signIn({
        onLoginSuccess: () => this.handleLoginSuccess(flowId),
        onLoginFailure: () => this.handleLoginClose(flowId)
      });
    } catch (error) {
      this.handleLoginClose(flowId);
      throw error;
    }
  }

  /**
   * 为活跃登录流程刷新一次会话与队伍，随后打开确认。
   * @param {number} flowId XPASS 成功回调捕获的登录流程标识。
   * @returns {Promise<undefined>} 共享的流程 Promise；挂载期间错误由 Guest Toast 负责。
   */
  @autobind
  handleLoginSuccess(flowId = this.activeLoginFlowId) {
    if (
      !this.isAuthenticating ||
      flowId !== this.activeLoginFlowId ||
      !this.isLifecycleActive(this.lifecycleGeneration) ||
      !this.inviteContext ||
      this.invalidInvite
    ) {
      return Promise.resolve(undefined);
    }
    if (this.loginSuccessPromise) {
      return this.loginSuccessPromise;
    }

    const generation = this.lifecycleGeneration;
    const requestOwner = { flowId, generation };
    this.activeLoginRequestOwner = requestOwner;
    const request = Promise.resolve()
      .then(() => this.activitySessionStore.refresh(this.inviteContext))
      .then(activityContext => {
        if (!this.isLoginFlowActive(flowId, generation)) {
          return undefined;
        }
        this.applyActivityContext(activityContext);
        return this.refreshGuestTeam(generation, requestOwner);
      })
      .then(viewModel => {
        if (!viewModel || !this.isLoginFlowActive(flowId, generation)) {
          return undefined;
        }
        this.isAuthenticating = false;
        this.activeLoginFlowId = 0;
        this.activeLoginRequestOwner = null;
        this.disposeLoginHandlers();
        return this.openJoinConfirm();
      })
      .catch(error => {
        if (!this.isLoginFlowActive(flowId, generation)) {
          return undefined;
        }
        this.isAuthenticating = false;
        this.activeLoginFlowId = 0;
        this.activeLoginRequestOwner = null;
        this.invalidInvite = true;
        this.loadStatus = 'error';
        this.disposeLoginHandlers();
        this.toast.fail(getErrorMessage(error));
        return undefined;
      });
    this.loginSuccessPromise = request;
    return request;
  }

  /**
   * 仅取消匹配的登录流程，保留 InviteContext 与 Team ViewModel 供重试。
   * @param {number} flowId XPASS 关闭/失败回调捕获的登录流程标识。
   * @returns {undefined}
   */
  @autobind
  handleLoginClose(flowId = this.activeLoginFlowId) {
    if (flowId && flowId !== this.activeLoginFlowId) {
      return undefined;
    }
    this.isAuthenticating = false;
    this.activeLoginFlowId = 0;
    this.activeLoginRequestOwner = null;
    this.loginSuccessPromise = null;
    if (this.loadStatus === 'loading' && this.teamViewModel.teamId) {
      this.loadStatus = 'ready';
    }
    this.disposeLoginHandlers();
    return undefined;
  }

  /** 打开确认弹窗，以第一位权威成员作为邀请人展示。 */
  @autobind
  openJoinConfirm() {
    if (this.joinDisabled || !this.joinTeamModal) {
      return undefined;
    }
    const firstMember = this.teamViewModel.members.find(Boolean);
    const inviterDisplayName = this.teamViewModel.inviterDisplayName || (firstMember ? firstMember.displayName : '');
    this.joinTeamModalStore.prepareConfirm(inviterDisplayName);
    this.joinTeamModal.open();
    return undefined;
  }

  /**
   * 不可逆的入队只执行一次，之后只接受权威刷新回来的队伍。
   * @returns {Promise<'joined'|'retryable-failure'|'committed-refresh-failure'>} 共享的弹窗结果；挂载期间的错误由 Guest 负责。
   */
  @autobind
  joinInvitedTeam() {
    if (this.joinPromise) {
      return this.joinPromise;
    }
    if (this.joinCommitted) {
      return Promise.resolve('committed-refresh-failure');
    }
    if (this.joinDisabled) {
      return Promise.resolve('retryable-failure');
    }
    const previousViewModel = this.teamViewModel;
    const generation = this.lifecycleGeneration;
    this.isJoining = true;
    let mutationCommitted = false;
    let mutationRequest;
    try {
      mutationRequest = this.teamService.joinGuestTeam(this.inviteContext.fromTeamId);
    } catch (error) {
      mutationRequest = Promise.reject(error);
    }
    const request = Promise.resolve(mutationRequest)
      .then(() => {
        mutationCommitted = true;
        // 服务端提交即视为已拥有队伍：立即登记用户级事实（入队目标即自己的队伍），
        // 分享 reaction 随之重推客态——成功弹窗停留 Guest 页（stayAfterJoin 语义）也能正确分享
        this.teamFactStore.update({ hasTeam: true, teamId: this.inviteContext.fromTeamId });
        const trackerArgs = this.getGuestTrackerArgs();
        if (trackerArgs) {
          $$tracker.clickConfirmJoinTeam(...trackerArgs, 'success', '');
        }
        // 服务端提交不可逆，必须跨 UI 代际存活，防止二次入队
        this.joinCommitted = true;
        if (!this.isLifecycleActive(generation)) {
          return undefined;
        }
        return this.refreshGuestTeam(generation);
      })
      .then(viewModel => (viewModel && this.isLifecycleActive(generation) ? 'joined' : 'committed-refresh-failure'))
      .catch(error => {
        if (!this.isLifecycleActive(generation)) {
          return mutationCommitted ? 'committed-refresh-failure' : 'retryable-failure';
        }
        this.teamViewModel = previousViewModel;
        this.loadStatus = 'ready';
        this.toast.fail(getErrorMessage(error));
        const trackerArgs = this.getGuestTrackerArgs();
        if (!mutationCommitted && trackerArgs) {
          $$tracker.clickConfirmJoinTeam(...trackerArgs, 'fail', getJoinTeamFailureReason(error));
        }
        return mutationCommitted ? 'committed-refresh-failure' : 'retryable-failure';
      })
      .then(result => {
        if (this.joinPromise === request) {
          this.isJoining = false;
          this.joinPromise = null;
        }
        return result;
      });
    this.joinPromise = request;
    return request;
  }

  /**
   * 新玩家仅携带受控 InviteContext 字段跳转；老玩家打开页面持有的召回弹窗。
   * @returns {undefined} 身份未知被拦截并由页面 owner 提示。
   */
  @autobind
  handleContinueInvite() {
    if (!this.joined) {
      return undefined;
    }

    $$tracker.clickInviteEntry('邀请入队');

    const playerType = this.activitySessionStore.playerType;
    if (playerType === 'new') {
      this.enterPosterFromGuest();
      return undefined;
    }
    if (playerType === 'veteran') {
      if (this.friendRecallModal) {
        // 2026-09-24 需求：先门控取召回列表，无可召回好友时不展示弹窗直接进海报页
        this.friendRecallModalStore.openRecallGate(() => this.friendRecallModal.open());
      }
      return undefined;
    }
    this.toast.fail(UNKNOWN_PLAYER_MESSAGE);
    return undefined;
  }

  /** 仅携带受控的 Guest 邀请上下文进入既有海报页。 */
  @autobind
  enterPosterFromGuest() {
    const context = this.inviteContext || {};
    const trackerContext = this.trackerContext || {};
    const entrySource = ['poster_qr', 'invite_link', 'recall_sms'].indexOf(trackerContext.source) !== -1 ? trackerContext.source : context.entrySource || '';
    this.redirect(
      formatRoute(routeConfigs.ENDED_POSTER.path, {
        source: 'GUEST',
        entry_source: entrySource,
        from_team_id: context.fromTeamId || '',
        share_code: context.shareCode || ''
      })
    );
    return undefined;
  }

  /** Guest 建队入口路由到活动首页，交由身份分流。 */
  @autobind
  handleCreateTeamClick() {
    if (!this.createDisabled) {
      this.navigateToHome();
    }
    return undefined;
  }

  /**
   * Routes both success and creation exits to the activity home（PRD 03 状态-3-9）：
   * 不再直落组队页——首页 CTA 按身份分流（未完成回顾的老玩家先进回顾页），
   * 同时保证首页底栏的回顾前置拦截链路不被绕过（2026-09-17 用户决策）。
   */
  @autobind
  navigateToHome() {
    this.redirect(routeConfigs.MAIN.getFormatPath());
    return undefined;
  }

  /** 关闭成功弹窗，同时保持 Guest 挂载在已入队 CTA 状态。 */
  @autobind
  stayAfterJoin() {
    this.joined = true;
    if (this.joinTeamModal) {
      this.joinTeamModal.close();
    }
    return undefined;
  }

  /**
   * 安装流程级的手动关闭回调并记录归属，保证安全清理。
   * @param {number} flowId 活跃登录流程标识。
   * @returns {undefined}
   */
  bindLoginHandlers(flowId) {
    const xpass = getRuntimeXpass();
    if (xpass) {
      this.manualLoginCloseHandler = () => this.handleLoginClose(flowId);
      xpass.onManualCloseModal = this.manualLoginCloseHandler;
    }
    return undefined;
  }

  /**
   * Guest 读写队伍状态前，只接受活跃会话契约。
   * @param {{activityStage:string,playerType:string}|undefined} activityContext 共享会话结果。
   * @returns {undefined}
   */
  applyActivityContext(activityContext) {
    if (!activityContext || activityContext.activityStage !== 'active') {
      // 活动阶段已知时按阶段提示（邀请链接本身有效，误导重开链接无意义）；无上下文才回落邀请无效兜底
      throw new Error((activityContext && ACTIVITY_STAGE_MESSAGES[activityContext.activityStage]) || INVALID_INVITE_MESSAGE);
    }
    this.activityStage = activityContext.activityStage;
    return undefined;
  }

  /**
   * 开启新的挂载代际，此前的异步回调无法写入重挂载后的页面。
   * @returns {number} 活跃的代际标识。
   */
  activateLifecycle() {
    this.lifecycleGeneration += 1;
    this.isDisposed = false;
    return this.lifecycleGeneration;
  }

  /**
   * 使匹配的挂载失效，只释放页面持有的回调/Promise 引用。
   * @param {number} generation activateLifecycle 返回的代际。
   * @returns {undefined}
   */
  disposeLifecycle(generation) {
    if (generation !== this.lifecycleGeneration) {
      return undefined;
    }
    this.isDisposed = true;
    this.isAuthenticating = false;
    this.activeLoginFlowId = 0;
    this.activeLoginRequestOwner = null;
    this.initializePromise = null;
    this.initializeGeneration = null;
    this.loginSuccessPromise = null;
    this.disposeLoginHandlers();
    return undefined;
  }

  /** @returns {boolean} 传入代际是否仍可提交页面状态。 */
  isLifecycleActive(generation) {
    return !this.isDisposed && generation === this.lifecycleGeneration;
  }

  /** @returns {boolean} 挂载与登录流程是否仍各自持有回调。 */
  isLoginFlowActive(flowId, generation) {
    return this.isAuthenticating && flowId === this.activeLoginFlowId && this.isLifecycleActive(generation);
  }

  /**
   * 校验挂载归属，且在存在登录请求 token 时校验其仍允许提交队伍状态。
   * @param {number} generation 请求发起前捕获的挂载代际。
   * @param {object|null} requestOwner 可选的登录请求 token。
   * @returns {boolean} 响应是否允许更新 Guest 状态。
   */
  isRequestOwnerActive(generation, requestOwner) {
    return this.isLifecycleActive(generation) && (!requestOwner || requestOwner === this.activeLoginRequestOwner);
  }

  /** 仅当 SDK 回调仍归属本 GuestStore 时才移除。 */
  @autobind
  disposeLoginHandlers() {
    const xpass = getRuntimeXpass();
    if (xpass && this.manualLoginCloseHandler && xpass.onManualCloseModal === this.manualLoginCloseHandler) {
      xpass.onManualCloseModal = undefined;
    }
    this.manualLoginCloseHandler = null;
    return undefined;
  }

  /**
   * Guest 导航只接受由路由配置派生的内部 URL，含受控 query 参数。
   * @param {string} url 路由配置派生的内部 URL。
   * @returns {undefined}
   */
  redirect(url) {
    this.$history.push(url);
    return undefined;
  }
}

export default GuestStore;
